import { ReactElement, useState } from 'react';
import { connect } from 'react-redux';
import { UserSettingService } from 'services/UserSettingService';
import {
    AppPrimaryButton,
    AppSecondaryButton,
} from 'shared/components/AppComponents';
import { Utils } from 'shared/helpers';
import { ProfileActions } from 'state/profile/Action';
import TitleCard from '../TitleCard';
import TwoFactorConfirmationDialog from '../TwoFactorConfirmationDialog';
import TwoFactorEnableDisableDialog from '../TwoFactorEnableDisableDialog';
import TwoFactorResetDialog from '../TwoFactorResetDialog';
import { Box, Grid } from '@mui/material';

const TwoFactorForm = (props: any): ReactElement => {
    const { profileInfo } = props;
    const { enabled, setupCompleted } =
        profileInfo.userInfo.twoFactorAuthentication;
    const [showTwoFactor, setShowTwoFactor] = useState(false);
    const [loader, setLoader] = useState(false);
    const [twoFASetupData, setTwoFASetupData] = useState({
        url: '',
        secret: '',
    });
    const [twoFaProgress, setTwoFaProgress] = useState(false);
    const [isResetDialogOpened, setIsResetDialogOpened] = useState(false);
    const [resetConfirmation, setResetConfirmation] = useState(false);

    // API Calls
    const setupTwoFA = (callback: any): void => {
        setLoader(true);
        UserSettingService.setupTwoFactor()
            .then((response: any) => {
                const result = response.data.data;
                setLoader(false);
                callback(true, result);
                setShowTwoFactor(true);
            })
            .catch((error: any) => {
                Utils.handleErrorResponse(error);
                setLoader(false);
                callback(false);
            });
    };

    // UI Actions
    const actionSetupTwoFA = (): void => {
        setupTwoFA((status: any, response: any) => {
            if (status) {
                setTwoFASetupData(response);
                setTwoFaProgress(true);
            }
        });
    };

    const actionOpenResetPopup = (): void => {
        setIsResetDialogOpened(true);
    };

    const actionOnCancel = (): void => {
        setIsResetDialogOpened(false);
        setResetConfirmation(false);
    };

    // UI Elements

    return (
        <>
            <Box className="">
                <Box className="app-box-wrapper">
                    <Box className="app-box-header ">
                        <Box className="p-2">
                            {setupCompleted === false &&
                                enabled === false &&
                                twoFaProgress === false && (
                                    <>
                                        <TitleCard
                                            title="Two factor authentication"
                                            subTitle="Two factor authentication is a
                                                security feature that helps
                                                protect your account."
                                        />

                                        <Box className="mt-7">
                                            <AppPrimaryButton
                                                title="Setup 2FA"
                                                onClick={actionSetupTwoFA}
                                                className="btn-small poppins-semi-bold "
                                                loadingSize={22}
                                                isLoading={loader}
                                                disabled={loader}
                                                loadingText="SETTING"
                                                testid="submit2fA"
                                                aria-haspopup="dialog"
                                            />
                                        </Box>
                                    </>
                                )}

                            {enabled === true && setupCompleted === true && (
                                <>
                                    <TitleCard
                                        title="Security 2FA"
                                        subTitle=" You have activated the Two Factor Authentication. 2FA is a security
                feature that helps to protect your account."
                                    />{' '}
                                    <Box mt={4}>
                                        <Grid container spacing={4}>
                                            <Grid item lg={3} md={6} sm={12}>
                                                <AppSecondaryButton
                                                    title="Enabled"
                                                    fullWidth
                                                    disabled
                                                    className="btn-small"
                                                />
                                            </Grid>
                                            <Grid item lg={3} md={6} sm={12}>
                                                <AppPrimaryButton
                                                    title="Disable 2FA"
                                                    fullWidth
                                                    onClick={
                                                        actionOpenResetPopup
                                                    }
                                                    className="btn-small"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>{' '}
                                </>
                            )}

                            {setupCompleted === true && enabled === false && (
                                <div>
                                    <TitleCard
                                        title="Security 2FA"
                                        subTitle="Enable Two Factor Authentication using Google Authenticator. 2FA
              is a security feature that helps to protect your account."
                                    />

                                    <Box mt={4}>
                                        <Grid container spacing={4}>
                                            <Grid item lg={3} md={6} sm={12}>
                                                <AppSecondaryButton
                                                    title="Disabled"
                                                    fullWidth
                                                    disabled
                                                    className="btn-small"
                                                />
                                            </Grid>
                                            <Grid item lg={3} md={6} sm={12}>
                                                <AppPrimaryButton
                                                    title="Enable 2FA"
                                                    fullWidth
                                                    onClick={
                                                        actionOpenResetPopup
                                                    }
                                                    className="btn-small"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </div>
                            )}

                            {setupCompleted === true || enabled === true ? (
                                <Box mt={8}>
                                    <TitleCard
                                        title="Reset 2FA"
                                        subTitle="Reset your Two Factor Authentication."
                                    />

                                    <Box mt={5} mb={3}>
                                        <Grid container spacing={1}>
                                            <Grid
                                                item
                                                lg={3}
                                                md={4}
                                                sm={6}
                                                xs={12}
                                            >
                                                <AppPrimaryButton
                                                    className="btn-large"
                                                    title="Reset 2FA"
                                                    onClick={() =>
                                                        setResetConfirmation(
                                                            true
                                                        )
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Box>
                            ) : (
                                <Box />
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
            {showTwoFactor && (
                <TwoFactorConfirmationDialog
                    showForm={showTwoFactor}
                    onCloseForm={() => {
                        setShowTwoFactor(false);
                        setTwoFaProgress(false);
                    }}
                    twoFASetupData={twoFASetupData}
                    {...props}
                />
            )}

            {isResetDialogOpened && (
                <TwoFactorEnableDisableDialog
                    showForm={isResetDialogOpened}
                    onCloseForm={actionOnCancel}
                    {...props}
                />
            )}

            {resetConfirmation && (
                <TwoFactorResetDialog
                    showForm={resetConfirmation}
                    onCloseForm={actionOnCancel}
                    {...props}
                />
            )}
        </>
    );
};

const mapStateToProps = (state: any): any => ({
    profileInfo: state.profileState,
});

const mapDispatchToProps = (dispatch: any): any => ({
    update2FAStatus: (data: any) =>
        dispatch(ProfileActions.update2FAStatus(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(TwoFactorForm);
